import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
import {
  isUsableCouponStatus,
  canShowVoucherCashRedeem,
  isVoucherCashRedeemPending,
} from "./normalizeUserCoupon";
import { formatCouponExpireCountdown } from "@/components/Pages/aicopy/utils/couponPositionFields";
import { resolveVoucherRulesContent } from "./voucherRules";
import { canShowVoucherUse, getVoucherNavigateButtonLabelId, navigateVoucherUse } from "./voucherUseJump";
import VoucherRulesModal from "./VoucherRulesModal";
import VoucherCashRedeemModal from "./VoucherCashRedeemModal";

const { useIntl } = Hooks;

/** formatMessage 未命中时可能原样返回 id，用 defaultMessage 兜底 */
function formatVoucherMsg(intl, id, defaultMessage, values) {
  if (!intl?.formatMessage) return defaultMessage;
  try {
    const msg = intl.formatMessage({ id, defaultMessage }, values);
    if (msg && msg !== id) return msg;
  } catch {
    /* ignore */
  }
  return defaultMessage;
}

import cardInnerBg from "@/assets/images/activity/voucher/card-inner-bg.svg";
import voucherTicketIcon from "@/assets/images/rewards/voucher-ticket.svg";
import styles from "./index.module.less";

export { buildVoucherAicopyUseUrl, canShowVoucherUse, canShowVoucherAicopyUse } from "./voucherUseJump";
export { canShowVoucherCashRedeem } from "./normalizeUserCoupon";

export function getVoucherStatusMeta(status, t) {
  const s = String(status || "").toUpperCase();
  const map = {
    AVAILABLE: {
      label: t("activity.voucherStatusAvailable") || "Available",
      tagBg: "rgba(46,189,133,0.1)",
      tagColor: "#2EBD85",
      innerBottomBg: "rgba(33, 35, 41, 0.1)",
    },
    LOCKED: {
      label: t("activity.voucherStatusLocked") || "In use",
      tagBg: "rgba(254, 190, 64, 0.15)",
      tagColor: "#FEBE40",
      innerBottomBg: "rgba(254, 190, 64, 0.08)",
    },
    CONSUMED: {
      label: t("activity.voucherStatusConsumed") || "Used",
      tagBg: "rgba(145,145,145,0.1)",
      tagColor: "#7A7A7A",
      innerBottomBg: "rgba(145,145,145,0.1)",
    },
    EXPIRED: {
      label: t("activity.voucherStatusExpired") || "Expired",
      tagBg: "rgba(145,145,145,0.1)",
      tagColor: "#7A7A7A",
      innerBottomBg: "rgba(145,145,145,0.1)",
    },
    VOID: {
      label: t("activity.voucherStatusVoid") || "Void",
      tagBg: "rgba(145,145,145,0.1)",
      tagColor: "#7A7A7A",
      innerBottomBg: "rgba(145,145,145,0.1)",
    },
  };
  return (
    map[s] || {
      label: s || "—",
      tagBg: "rgba(255,255,255,0.08)",
      tagColor: "rgba(255,255,255,0.4)",
      innerBottomBg: "rgba(33, 35, 41, 0.1)",
    }
  );
}

/**
 * 代金券卡片（与 Voucher 列表页一致）
 * @param {{ item: object, t: Function, showAicopyUse?: boolean, layout?: "list"|"modal" }} props
 */
const VoucherLockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <rect x="2.25" y="5.25" width="7.5" height="5.25" rx="1.2" stroke="#FEBE40" strokeWidth="1" />
    <path d="M4 5.25V3.75C4 2.645 4.895 1.75 6 1.75C7.105 1.75 8 2.645 8 3.75V5.25" stroke="#FEBE40" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

const VoucherRulesInfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
      stroke="white"
      strokeOpacity="0.5"
      strokeWidth="0.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.09272 3C6.66667 3 7.12583 3.15011 7.47903 3.46799C7.8234 3.77704 8 4.20088 8 4.73068C8 5.16336 7.88521 5.52539 7.67329 5.80795C7.59382 5.90508 7.34658 6.13466 6.92274 6.49669C6.7638 6.63797 6.64018 6.78808 6.56071 6.94702C6.46358 7.12362 6.41943 7.31788 6.41943 7.53863V7.68874H5.47461V7.53863C5.47461 7.21192 5.52759 6.92936 5.64238 6.69978C5.766 6.45254 6.10155 6.09051 6.64901 5.60486L6.79912 5.43709C6.95806 5.234 7.03753 5.02208 7.03753 4.79249C7.03753 4.48344 6.94923 4.24503 6.78146 4.06843C6.60486 3.89183 6.35762 3.80353 6.03974 3.80353C5.64238 3.80353 5.35099 3.92715 5.17439 4.17439C5.01545 4.38631 4.93598 4.69536 4.93598 5.08389H4C4 4.43929 4.18543 3.93598 4.56512 3.56512C4.93598 3.18543 5.44812 3 6.09272 3ZM5.94261 8.16556C6.12804 8.16556 6.28698 8.21854 6.4106 8.34216C6.53422 8.45695 6.59603 8.60706 6.59603 8.79249C6.59603 8.97793 6.53422 9.12804 6.4106 9.25166C6.27815 9.36645 6.11921 9.42826 5.94261 9.42826C5.75718 9.42826 5.60706 9.36645 5.49227 9.24283C5.35982 9.11921 5.29801 8.9691 5.29801 8.79249C5.29801 8.60706 5.35982 8.45695 5.49227 8.34216C5.60706 8.21854 5.75718 8.16556 5.94261 8.16556Z"
      fill="white"
      fillOpacity="0.5"
    />
  </svg>
);

export default function VoucherCard({
  item,
  t,
  showAicopyUse = true,
  layout = "list",
  isRewardsPc = false,
  onCashRedeemSuccess,
}) {
  const router = useRouter();
  const intl = useIntl();
  const [rulesOpen, setRulesOpen] = useState(false);
  const [cashRedeemOpen, setCashRedeemOpen] = useState(false);
  const statusKey = (item.status || "").toUpperCase();
  const status = getVoucherStatusMeta(statusKey, t);
  const usable = isUsableCouponStatus(statusKey);
  const canShowCashRedeem = showAicopyUse && canShowVoucherCashRedeem(item);
  const cashRedeemPending = showAicopyUse && isVoucherCashRedeemPending(item);
  const canShowNavigateUse = canShowVoucherUse(item, showAicopyUse);

  const cashEffectiveAtRaw = item?.raw?.effectiveAt ?? item?.raw?.effective_at ?? item?.effectiveAt;
  const [cashPendingTick, setCashPendingTick] = useState(0);
  useEffect(() => {
    if (!cashRedeemPending || !cashEffectiveAtRaw) return undefined;
    const id = setInterval(() => setCashPendingTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [cashRedeemPending, cashEffectiveAtRaw]);

  const cashRedeemPendingUi = useMemo(() => {
    if (!cashRedeemPending || !cashEffectiveAtRaw) return null;
    const effMs = new Date(cashEffectiveAtRaw).getTime();
    if (!Number.isFinite(effMs)) return null;
    const remaining = effMs - Date.now();
    const { text: countdown } = formatCouponExpireCountdown(remaining);
    return { countdown };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cashPendingTick drives countdown refresh
  }, [cashRedeemPending, cashEffectiveAtRaw, cashPendingTick]);

  const handleUseAicopy = (e) => {
    e.stopPropagation();
    navigateVoucherUse(item, { router });
  };

  const handleOpenCashRedeem = (e) => {
    e.stopPropagation();
    setCashRedeemOpen(true);
  };

  const voucherName = item.displayDescription || item.name || item.couponName;

  const rulesContent = useMemo(() => {
    const locale = intl?.locale || router?.locale;
    const fromApi = resolveVoucherRulesContent(item.raw, locale);
    if (fromApi) return fromApi;
    const fallback = t("aicopy.assetRulesContent");
    return fallback && fallback !== "aicopy.assetRulesContent" ? fallback : "";
  }, [item.raw, intl?.locale, router?.locale, t]);

  const handleOpenRules = (e) => {
    e.stopPropagation();
    setRulesOpen(true);
  };

  const lockedStatus = cashRedeemPending && cashRedeemPendingUi ? (
    <span className={cx(styles.statusLocked, isRewardsPc && layout === "list" && styles.pcVoucherStatusLocked)}>
      <VoucherLockIcon />
      <span>{cashRedeemPendingUi.countdown}</span>
    </span>
  ) : null;

  const expiryText = formatVoucherMsg(intl, "activity.voucherExpiresOn", "Expires on {time}", {
    time: item.expireTime || item.expiryDate || "—",
  });

  if (isRewardsPc && layout === "list") {
    const hasAmount = item.amount != null && String(item.amount).trim() !== "";
    const titleText = hasAmount
      ? `${item.amount}${item.unit ? ` ${item.unit}` : ""}`
      : voucherName;
    const showNameLine = hasAmount && voucherName && voucherName !== titleText;

    return (
      <>
        <li
          className={cx(styles.pcVoucherRow, !usable && styles.pcVoucherRowDim)}
        >
          <span className={styles.pcVoucherTicket} aria-hidden>
            <img src={voucherTicketIcon} alt="" className={styles.pcVoucherTicketIcon} />
          </span>
          <div className={styles.pcVoucherMain}>
            {hasAmount && showNameLine ? (
              <div className={styles.pcVoucherAmount}>{titleText}</div>
            ) : null}
            <div className={styles.pcVoucherNameRow}>
              <span className={styles.pcVoucherName}>
                {hasAmount && showNameLine ? voucherName : titleText}
              </span>
              {lockedStatus || (
                <span
                  className={styles.pcVoucherStatus}
                  style={{ background: status.tagBg, color: status.tagColor }}
                >
                  {status.label}
                </span>
              )}
            </div>
            <div className={styles.pcVoucherExpiry}>{expiryText}</div>
          </div>
          <div className={styles.pcVoucherActions}>
            <button
              type="button"
              className={styles.pcVoucherRulesBtn}
              aria-label={formatVoucherMsg(intl, "activity.voucherRuleDetailsTitle", "Rule Details")}
              onClick={handleOpenRules}
            >
              <VoucherRulesInfoIcon />
            </button>
            {canShowCashRedeem ? (
              <button type="button" className={styles.pcVoucherUseBtn} onClick={handleOpenCashRedeem}>
                {formatVoucherMsg(intl, "activity.voucherCashRedeem", "Redeem")}
              </button>
            ) : canShowNavigateUse ? (
              <button type="button" className={styles.pcVoucherUseBtn} onClick={handleUseAicopy}>
                {formatVoucherMsg(
                  intl,
                  getVoucherNavigateButtonLabelId(item),
                  getVoucherNavigateButtonLabelId(item) === "activity.voucherTrade" ? "Trade" : "Use",
                )}
              </button>
            ) : null}
          </div>
        </li>
        <VoucherRulesModal open={rulesOpen} content={rulesContent} onClose={() => setRulesOpen(false)} t={t} />
        <VoucherCashRedeemModal
          open={cashRedeemOpen}
          coupon={item}
          onClose={() => setCashRedeemOpen(false)}
          onSuccess={onCashRedeemSuccess}
        />
      </>
    );
  }

  return (
    <div
      className={cx(
        styles.card,
        (canShowNavigateUse || canShowCashRedeem) && styles.cardHasUseBtn,
        !usable && styles.cardDim,
        layout === "modal" && styles.cardInModal
      )}
    >
      <img className={styles.cardBgImage} src={cardInnerBg} alt="" />
      <div className={styles.cardInner}>
        <div className={styles.cardInnerBottomShade} style={{ background: status.innerBottomBg }} />
      </div>

      {lockedStatus ? (
        <div className={cx(styles.cornerTag, styles.cornerTagLocked)}>{lockedStatus}</div>
      ) : (
        <div className={styles.cornerTag} style={{ background: status.tagBg, color: status.tagColor }}>
          {status.label}
        </div>
      )}

      <div className={styles.cardSeparator} />

      <div className={styles.cardContent}>
        <div className={styles.cardMainBody}>
          <div className={styles.cardAmount}>
            {item.amount}
            {item.unit ? <span className={styles.cardAmountUnit}> {item.unit}</span> : null}
          </div>
          <div className={styles.cardNameRow}>
            <span className={styles.cardVoucherName} title={voucherName}>
              {voucherName}
            </span>
            <button
              type="button"
              className={styles.rulesInfoBtn}
              aria-label={formatVoucherMsg(intl, "activity.voucherRuleDetailsTitle", "Rule Details")}
              onClick={handleOpenRules}
            >
              <VoucherRulesInfoIcon />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.cardFooter}>
        <div className={styles.cardExpiry}>{expiryText}</div>
        {canShowCashRedeem ? (
          <button type="button" className={styles.useBtn} onClick={handleOpenCashRedeem}>
            {formatVoucherMsg(intl, "activity.voucherCashRedeem", "Redeem")}
          </button>
        ) : canShowNavigateUse ? (
          <button type="button" className={styles.useBtn} onClick={handleUseAicopy}>
            {formatVoucherMsg(
              intl,
              getVoucherNavigateButtonLabelId(item),
              getVoucherNavigateButtonLabelId(item) === "activity.voucherTrade" ? "Trade" : "Use",
            )}
          </button>
        ) : null}
      </div>

      <VoucherRulesModal open={rulesOpen} content={rulesContent} onClose={() => setRulesOpen(false)} t={t} />
      <VoucherCashRedeemModal
        open={cashRedeemOpen}
        coupon={item}
        onClose={() => setCashRedeemOpen(false)}
        onSuccess={onCashRedeemSuccess}
      />
    </div>
  );
}
